import {
  ChangeEvent,
  MouseEvent,
  useCallback,
  KeyboardEvent,
  useState,
  useRef,
  CSSProperties,
  ClipboardEvent,
  RefObject,
  FocusEvent,
  SetStateAction,
  Dispatch,
} from "react";

import { Grid } from "@mui/material";
import classNames from "classnames";

import { AmplitudeEvents } from "../../../../../constants/amplitude";
import { useAmplitude } from "../../../../../hooks/amplitude/useAmplitude";
import { useTranslate } from "../../../../../hooks/useTranslate";
import { EmailChip } from "../EmailChip/EmailChip";
import { extractEmailsFromString } from "../helpers";
import { Root, classes } from "../styles";
import { EmailsList } from "../types";

import { DashboardButton } from "@/dashboard-design-system/Button/DashboardButton";
import { DashboardIconButton } from "@/dashboard-design-system/IconButton";
import { DashboardLink } from "@/dashboard-design-system/Link";
import { Cross as Close, Add } from "@/icons/outlined";

interface EmailRecipientsChipListProps {
  setEmailsList: Dispatch<SetStateAction<EmailsList>>;
  emailsList: EmailsList;
  parseAndAddEmails: (input: string) => void;
  allEmails: {
    faultyEmails: string[];
    validEmails: string[];
    unsubscribeEmails: string[];
  };
  disabled?: boolean;
  inputStyle?: CSSProperties;
  inputContainerRef?: RefObject<HTMLDivElement | null> | undefined;
  inputOnly?: boolean;
  onAddClick?: () => void;
}

export const EmailRecipientsChipList = ({
  setEmailsList,
  emailsList,
  parseAndAddEmails,
  allEmails,
  disabled,
  inputStyle,
  inputContainerRef,
  inputOnly,
  onAddClick,
}: EmailRecipientsChipListProps) => {
  const { t } = useTranslate();
  const { logAmplitudeEvent } = useAmplitude();

  const inputRef = useRef<HTMLInputElement>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const sortedEmailsAfterValidation: { email: string; isFaulty?: boolean; isUnsubscribed?: boolean }[] = [
    ...allEmails.faultyEmails.map((email) => ({ email, isFaulty: true })),
    ...allEmails.unsubscribeEmails.map((email) => ({ email, isUnsubscribed: true })),
    ...allEmails.validEmails.map((email) => ({ email })),
  ];

  const [seeAllEmails, setSeeAllEmails] = useState(false);
  const displayMoreEmailLimit = 500;
  const hiddenEmailsCount = sortedEmailsAfterValidation.length - displayMoreEmailLimit;
  const displaySeeMore = hiddenEmailsCount > 0 && !seeAllEmails;
  const displayedEmails = seeAllEmails
    ? sortedEmailsAfterValidation
    : sortedEmailsAfterValidation.slice(0, displayMoreEmailLimit);

  const handleDisplayAllEmails = useCallback(() => {
    setSeeAllEmails(true);
  }, []);

  const handleHideAllEmails = useCallback((event: FocusEvent<HTMLDivElement, Element>) => {
    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
      setSeeAllEmails(false);
    }
  }, []);

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  }, []);

  const handleRootFocus = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const suppressClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);

  const handleChipDelete = useCallback(
    (emailToRemove: string) => {
      logAmplitudeEvent(AmplitudeEvents.EmailRecipientRemoved);
      setEmailsList((prevEmailList) => {
        const newEmailsList: EmailsList = {};
        Object.keys(prevEmailList).forEach((email) => {
          if (email !== emailToRemove) {
            newEmailsList[email] = prevEmailList[email];
          }
        });
        return newEmailsList;
      });
    },
    [logAmplitudeEvent, setEmailsList]
  );

  const submitEmails = useCallback(() => {
    parseAndAddEmails(inputValue);
    // Clear the input value once we have added the emails
    setInputValue("");
  }, [inputValue, parseAndAddEmails]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      // We parse the input value when the user presses Enter
      if (event.key === "Enter") {
        submitEmails();
        return;
      }
      // We delete the last email chip when the user presses Backspace or Delete
      // when the input field is empty
      if ((event.key === "Backspace" || event.key === "Delete") && !event.currentTarget.value) {
        const listToDeleteFrom = allEmails.validEmails.length > 0 ? allEmails.validEmails : allEmails.faultyEmails;
        if (listToDeleteFrom.length > 0) {
          const lastEmail = listToDeleteFrom[listToDeleteFrom.length - 1];
          handleChipDelete(lastEmail);
          return;
        }
      }
    },
    [allEmails, handleChipDelete, submitEmails]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent<HTMLInputElement>) => {
      // We preventDefault to not trigger the input change event
      event.preventDefault();

      // Get the pasted text
      const pastedText = (event.clipboardData || window.clipboardData).getData("text");

      const newEmails = extractEmailsFromString(pastedText.trim());
      const newEmailsKeys = Object.keys(newEmails);

      const hasFaultyEmails = newEmailsKeys.some((email) => !newEmails[email].valid);

      setEmailsList((prevEmailList) => ({ ...prevEmailList, ...newEmails }));

      if (hasFaultyEmails) {
        logAmplitudeEvent(AmplitudeEvents.EmailRecipientPasteAttemptedwithError);
      }
    },
    [logAmplitudeEvent, setEmailsList]
  );

  const onClearAll = () => {
    setEmailsList({});
  };

  const showClear = Object.keys(emailsList).length > 0;

  return (
    <Grid container alignItems="baseline">
      <Grid item xs sx={{ position: "relative" }}>
        <Root
          onFocus={handleRootFocus}
          onClick={handleRootFocus}
          className={classNames({
            [classes.error]: allEmails.faultyEmails.length > 0,
            [classes.rootWithClear]: showClear,
          })}
          onBlur={handleHideAllEmails}
          tabIndex={-1}
          ref={inputContainerRef}
          sx={(theme) => ({
            ...(disabled && {
              pointerEvents: "none",
              backgroundColor: theme.palette.surface.neutral.moderate,
            }),
          })}
          style={inputStyle}
        >
          <Grid item xs className={classes.chipContainer} data-test="email-recipient-chip-list">
            {displayedEmails.map(({ email, isFaulty, isUnsubscribed }) => (
              <EmailChip
                key={email}
                className={classes.chip}
                email={email}
                onDelete={handleChipDelete}
                disabled={disabled}
                isFaulty={isFaulty}
                isUnsubscribed={isUnsubscribed}
              />
            ))}

            {displaySeeMore && (
              <DashboardLink key="displayMore" vibe="brand" onClick={handleDisplayAllEmails} marginLeft={2}>
                {t("dashboard", "showNEmails", { count: hiddenEmailsCount })}
              </DashboardLink>
            )}
            {!disabled && (
              <>
                <input
                  defaultValue={inputValue}
                  value={inputValue}
                  onChange={handleInputChange}
                  className={classes.input}
                  ref={inputRef}
                  onClick={suppressClick}
                  placeholder={
                    Object.keys(emailsList).length === 0
                      ? t("dashboard", "emailDrawer.recipients.input.placeholder")
                      : undefined
                  }
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  onBlur={submitEmails}
                />
              </>
            )}
          </Grid>
          {showClear && (
            <Grid item>
              <DashboardIconButton size="small" vibe="neutral" onClick={onClearAll} className={classes.clear}>
                <Close />
              </DashboardIconButton>
            </Grid>
          )}
        </Root>
      </Grid>
      {!inputOnly && onAddClick && (
        <Grid item marginLeft={1}>
          <DashboardButton variant="outlined" vibe="brand" onClick={onAddClick} startIcon={<Add />}>
            {t("dashboard", "common.add")}
          </DashboardButton>
        </Grid>
      )}
    </Grid>
  );
};
